import { StaticPageLayout } from '@/layouts/static-page-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from '@/components/ui/carousel';
import { BookOpen, Target, Users, TrendingUp, Lightbulb, Brain, Calculator, Microscope, Globe, Scroll, Scale, Shield, Network, ArrowRight, History, Feather, Mountain, Landmark } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthProvider';
import * as d3 from 'd3';

export function KnowledgeBeaconsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, session } = useAuth();
  
  // Check if user is truly authenticated (has both user and valid session)
  const isAuthenticated = user && session;

  // Carousel state for auto-scroll
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  
  // State for expanded examples in entity cards
  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({});

  // Toggle expanded state for a specific card
  const toggleCardExpansion = (cardIndex: number) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardIndex]: !prev[cardIndex]
    }));
  };

  // Auto-scroll functionality for carousel
  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    setCount(carouselApi.scrollSnapList().length);
    setCurrent(carouselApi.selectedScrollSnap() + 1);

    carouselApi.on("select", () => {
      setCurrent(carouselApi.selectedScrollSnap() + 1);
    });

    // Auto-scroll every 5 seconds
    const autoScroll = setInterval(() => {
      carouselApi.scrollNext();
    }, 5000);

    // Clean up interval on component unmount
    return () => clearInterval(autoScroll);
  }, [carouselApi]);

  // Compact D3 Network for carousel cards - matching original style
  const CompactD3Network = ({ data, width = 400, height = 300 }: { data: any, width?: number, height?: number }) => {
    const svgRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
      if (!svgRef.current || !data) return;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      // Create arrow markers - adjusted for larger nodes
      svg.append("defs").selectAll("marker")
        .data(["compact-arrow"])
        .enter().append("marker")
        .attr("id", "compact-arrow")
        .attr("viewBox", "0 -5 10 10")
        .attr("refX", 60) // Increased for larger circles
        .attr("refY", 0)
        .attr("markerWidth", 6)
        .attr("markerHeight", 6)
        .attr("orient", "auto")
        .append("path")
        .attr("d", "M0,-5L10,0L0,5")
        .attr("fill", "#64748b");

      // Create simulation - optimized for larger canvas with better spacing
      const simulation = d3.forceSimulation(data.nodes)
        .force("link", d3.forceLink(data.links)
          .id((d: any) => d.id)
          .distance(280) // Increased for larger canvas
        )
        .force("charge", d3.forceManyBody().strength(-600)) // Stronger repulsion for larger space
        .force("center", d3.forceCenter(width / 2, height / 2))
        .force("collision", d3.forceCollide().radius(60)) // Larger collision radius
        .force("x", d3.forceX(width / 2).strength(0.1))
        .force("y", d3.forceY(height / 2).strength(0.1));

      // Create links - increased size for better visibility
      const link = svg.append("g")
        .attr("class", "links")
        .selectAll("line")
        .data(data.links)
        .enter().append("line")
        .attr("stroke", "#64748b") // Same color as original
        .attr("stroke-width", 4) // Increased from 3 for better visibility
        .attr("marker-end", "url(#compact-arrow)");

      // Create link labels - larger and more visible
      const linkLabel = svg.append("g")
        .attr("class", "link-labels")
        .selectAll("text")
        .data(data.links)
        .enter().append("text")
        .attr("class", "link-label")
        .attr("text-anchor", "middle")
        .attr("font-size", "12px") // Increased for better readability
        .attr("fill", "#4b5563") // Slightly darker for better contrast
        .attr("dy", -15) // Moved slightly up for thicker links
        .style("font-weight", "600") // Increased weight for better visibility
        .text((d: any) => d.label);

      // Create node groups - same structure as original
      const node = svg.append("g")
        .attr("class", "nodes")
        .selectAll("g")
        .data(data.nodes)
        .enter().append("g")
        .attr("class", "node");

      // Add circles to nodes - larger nodes to accommodate text properly
      node.append("circle")
        .attr("r", (d: any) => Math.max(d.val * 3.5, 50)) // Significantly larger nodes
        .attr("fill", (d: any) => d.color)
        .attr("stroke", "#fff")
        .attr("stroke-width", 3)
        .style("filter", "drop-shadow(2px 2px 4px rgba(0,0,0,0.2))");

      // Add main title labels inside nodes - modified to put second word on new line
      node.each(function(d: any) {
        const nodeGroup = d3.select(this);
        const words = d.name.split(/\s+/);
        const lines: string[] = [];

        // Put first word on first line, remaining words on subsequent lines
        if (words.length > 0) {
          lines.push(words[0]); // First word on first line
          
          if (words.length > 1) {
            // Put remaining words on new lines, but group them if they're short
            let currentLine = "";
            for (let i = 1; i < words.length; i++) {
              if (currentLine === "") {
                currentLine = words[i];
              } else if ((currentLine + " " + words[i]).length <= 12) {
                currentLine += " " + words[i];
              } else {
                lines.push(currentLine);
                currentLine = words[i];
              }
            }
            if (currentLine) lines.push(currentLine);
          }
        }

        // Add each line as a separate tspan - larger text for bigger nodes
        const textElement = nodeGroup.append("text")
          .attr("text-anchor", "middle")
          .attr("font-size", "14px") // Increased for larger nodes
          .attr("font-weight", "bold")
          .attr("fill", "#ffffff")
          .style("text-shadow", "2px 2px 4px rgba(0,0,0,0.9)")
          .style("pointer-events", "none");

        lines.forEach((line: string, i: number) => {
          textElement.append("tspan")
            .attr("x", 0)
            .attr("dy", i === 0 ? `${-0.4 * (lines.length - 1)}em` : "1.1em")
            .text(line);
        });
      });

      // Add type labels inside nodes - adjusted for larger nodes
      node.append("text")
        .attr("dx", 0)
        .attr("dy", "2.2em") // Adjusted for larger nodes
        .attr("text-anchor", "middle")
        .attr("font-size", "11px") // Increased for larger nodes
        .attr("font-weight", "500")
        .attr("fill", "#fbbf24") // Same golden yellow as original
        .style("text-shadow", "1px 1px 3px rgba(0,0,0,0.8)")
        .style("pointer-events", "none")
        .style("opacity", 1)
        .text((d: any) => d.type);

      // Update positions on simulation tick - adjusted for larger nodes
      simulation.on("tick", () => {
        const nodeRadius = 60; // Increased for larger nodes
        
        data.nodes.forEach((d: any) => {
          d.x = Math.max(nodeRadius, Math.min(width - nodeRadius, d.x));
          d.y = Math.max(nodeRadius, Math.min(height - nodeRadius, d.y));
        });

        link
          .attr("x1", (d: any) => d.source.x)
          .attr("y1", (d: any) => d.source.y)
          .attr("x2", (d: any) => d.target.x)
          .attr("y2", (d: any) => d.target.y);

        linkLabel
          .attr("x", (d: any) => (d.source.x + d.target.x) / 2)
          .attr("y", (d: any) => (d.source.y + d.target.y) / 2)
          .attr("transform", (d: any) => {
            const dx = d.target.x - d.source.x;
            const dy = d.target.y - d.source.y;
            const angle = Math.atan2(dy, dx) * 180 / Math.PI;
            const x = (d.source.x + d.target.x) / 2;
            const y = (d.source.y + d.target.y) / 2;
            
            // Keep text readable by flipping it if it would be upside down
            const adjustedAngle = (angle > 90 || angle < -90) ? angle + 180 : angle;
            
            return `rotate(${adjustedAngle}, ${x}, ${y})`;
          });

        node.attr("transform", (d: any) => `translate(${d.x},${d.y})`);
      });

      // Stop simulation after it settles - same as original
      setTimeout(() => {
        simulation.stop();
      }, 3000);

      // Clean up on unmount
      return () => {
        simulation.stop();
      };
    }, [data, width, height]);

    return (
      <svg 
        ref={svgRef}
        width={width}
        height={height}
        className="border rounded-lg bg-white dark:bg-gray-800" // Same background as original
      />
    );
  };

  // Pythagorean Theorem relationship data for force graph
  const graphData = {
    nodes: [
      {
        id: 'pythagorean-theorem',
        name: 'Pythagorean Theorem',
        type: 'Concept',
        subject: 'Mathematics',
        description: 'a² + b² = c² in right-angled triangles',
        color: '#8b5cf6',
        size: 25,
        val: 20
      },
      {
        id: 'cartography',
        name: 'Cartography',
        type: 'Example',
        subject: 'Geography', 
        description: 'Science of map-making using mathematical principles',
        color: '#10b981',
        size: 25,
        val: 15
      },
      {
        id: 'christopher-columbus',
        name: 'Christopher Columbus',
        type: 'Figure',
        subject: 'History',
        description: 'Explorer who used navigation calculations',
        color: '#f59e0b',
        size: 25,
        val: 15
      },
      {
        id: 'gis-systems',
        name: 'GIS Systems',
        type: 'Example',
        subject: 'Geography',
        description: 'Modern coordinate-based mapping systems',
        color: '#3b82f6',
        size: 25,
        val: 15
      }
    ],
    links: [
      {
        source: 'pythagorean-theorem',
        target: 'cartography',
        label: 'is essential in',
        color: '#64748b',
        width: 3
      },
      {
        source: 'pythagorean-theorem',
        target: 'christopher-columbus',
        label: 'relied on by',
        color: '#64748b',
        width: 3
      },
      {
        source: 'pythagorean-theorem',
        target: 'gis-systems',
        label: 'is a fundamental tool for',
        color: '#64748b',
        width: 3
      }
    ]
  };

  // Relationship examples for carousel
  const relationshipExamples = [
    {
      id: 1,
      title: "Pythagorean Theorem",
      beacon: {
        name: "Pythagorean Theorem",
        type: "Concept",
        subject: "Mathematics",
        description: "The fundamental relationship a² + b² = c² in right-angled triangles, discovered in ancient Greece.",
        icon: Calculator,
        color: "from-purple-500 to-indigo-500"
      },
      relationships: [
        {
          title: "Mathematical Foundation",
          description: "This theorem provides the mathematical basis for calculating distances and solving geometric problems.",
          type: "Core Relationship",
          icon: Network
        },
        {
          title: "Navigation Applications",
          description: "The theorem is essential in cartography and navigation, used by explorers like Christopher Columbus for voyage calculations.",
          type: "Cross-Subject Application",
          icon: Globe
        },
        {
          title: "Modern Technology",
          description: "Forms the foundation of GPS systems and computer graphics, bridging ancient mathematics with modern technology.",
          type: "Contemporary Relevance",
          icon: Target
        }
      ],
      graphData: {
        nodes: [
          { id: 'pythagorean', name: 'Pythagorean\nTheorem', type: 'Concept', color: '#8b5cf6', val: 18 },
          { id: 'cartography', name: 'Cartography', type: 'Example', color: '#10b981', val: 12 },
          { id: 'columbus', name: 'Christopher\nColumbus', type: 'Figure', color: '#f59e0b', val: 12 },
          { id: 'gps', name: 'GPS Systems', type: 'Example', color: '#3b82f6', val: 12 }
        ],
        links: [
          { source: 'pythagorean', target: 'cartography', label: 'enables' },
          { source: 'pythagorean', target: 'columbus', label: 'used by' },
          { source: 'pythagorean', target: 'gps', label: 'foundation of' }
        ]
      }
    },
    {
      id: 2,
      title: "French Revolution & Romantic Poetry",
      beacon: {
        name: "William Wordsworth",
        type: "Figure",
        subject: "English Literature",
        description: "A key figure in the development of Romantic Poetry, deeply influenced by the ideals of the French Revolution.",
        icon: Feather,
        color: "from-rose-500 to-pink-500"
      },
      relationships: [
        {
          title: "Literary Movement",
          description: "His poetry reflects the emotional and individualistic focus of Romanticism, often portraying Nature as Divine and Spiritual.",
          type: "Artistic Expression",
          icon: BookOpen
        },
        {
          title: "Historical Influence",
          description: "The French Revolution's social and political upheaval directly influenced Wordsworth's revolutionary approach to poetry.",
          type: "Cross-Subject Connection",
          icon: History
        },
        {
          title: "Philosophical Bridge",
          description: "Demonstrates the clear link between major historical events and literary movements, showing how History shapes Literature.",
          type: "Interdisciplinary Impact",
          icon: Lightbulb
        }
      ],
      graphData: {
        nodes: [
          { id: 'wordsworth', name: 'William\nWordsworth', type: 'Figure', color: '#f43f5e', val: 18 },
          { id: 'romanticism', name: 'Romantic\nPoetry', type: 'Concept', color: '#8b5cf6', val: 15 },
          { id: 'revolution', name: 'French\nRevolution', type: 'Event', color: '#dc2626', val: 15 },
          { id: 'nature', name: 'Nature as\nDivine', type: 'Concept', color: '#16a34a', val: 12 }
        ],
        links: [
          { source: 'revolution', target: 'wordsworth', label: 'influenced' },
          { source: 'wordsworth', target: 'romanticism', label: 'developed' },
          { source: 'romanticism', target: 'nature', label: 'portrays' }
        ]
      }
    },
    {
      id: 3,
      title: "Force and Elasticity",
      beacon: {
        name: "Physical Force",
        type: "Concept",
        subject: "Physics",
        description: "The influence that causes a change in motion of an object, including the restoring force principle in elasticity.",
        icon: Mountain,
        color: "from-blue-500 to-cyan-500"
      },
      relationships: [
        {
          title: "Scientific Principle",
          description: "Restoring force is key to elasticity - the ability of materials to return to original shape after deformation.",
          type: "Core Physics Concept",
          icon: Microscope
        },
        {
          title: "Engineering Application",
          description: "Essential for calculating elastic limits in construction, ensuring structural integrity of buildings and bridges.",
          type: "Cross-Subject Application",
          icon: Target
        },
        {
          title: "Real-World Impact",
          description: "Helps structures withstand forces like wind and earthquakes, bridging physics concepts with engineering solutions.",
          type: "Practical Implementation",
          icon: Shield
        }
      ],
      graphData: {
        nodes: [
          { id: 'force', name: 'Physical\nForce', type: 'Concept', color: '#3b82f6', val: 18 },
          { id: 'elasticity', name: 'Elasticity', type: 'Concept', color: '#8b5cf6', val: 15 },
          { id: 'engineering', name: 'Structural\nEngineering', type: 'Example', color: '#f59e0b', val: 15 },
          { id: 'buildings', name: 'Modern\nStructures', type: 'Example', color: '#10b981', val: 12 }
        ],
        links: [
          { source: 'force', target: 'elasticity', label: 'defines' },
          { source: 'elasticity', target: 'engineering', label: 'applied in' },
          { source: 'engineering', target: 'buildings', label: 'creates' }
        ]
      }
    },
    {
      id: 4,
      title: "Constitution & Fundamental Rights",
      beacon: {
        name: "Constitution of India",
        type: "Concept",
        subject: "Social Science",
        description: "The supreme law establishing government framework and defining fundamental rights of citizens.",
        icon: Landmark,
        color: "from-amber-500 to-orange-500"
      },
      relationships: [
        {
          title: "Philosophical Foundation",
          description: "Fundamental rights trace back to Enlightenment philosophers like John Locke and Jean Jacques Rousseau.",
          type: "Historical Roots",
          icon: Scroll
        },
        {
          title: "Independence Movement",
          description: "The struggle for rights was heavily influenced by India's Independence Movement, led by figures like Mahatma Gandhi.",
          type: "Cross-Subject Connection",
          icon: Users
        },
        {
          title: "Democratic Society",
          description: "These constitutional rights are essential for healthy civil society, linking historical movements with legal frameworks.",
          type: "Societal Impact",
          icon: Scale
        }
      ],
      graphData: {
        nodes: [
          { id: 'constitution', name: 'Constitution\nof India', type: 'Concept', color: '#f59e0b', val: 18 },
          { id: 'rights', name: 'Fundamental\nRights', type: 'Concept', color: '#8b5cf6', val: 15 },
          { id: 'enlightenment', name: 'Enlightenment\nPhilosophy', type: 'Concept', color: '#3b82f6', val: 17 },
          { id: 'gandhi', name: 'Mahatma\nGandhi', type: 'Figure', color: '#10b981', val: 15 },
          { id: 'society', name: 'Civil\nSociety', type: 'Concept', color: '#dc2626', val: 15 }
        ],
        links: [
          { source: 'enlightenment', target: 'rights', label: 'inspired' },
          { source: 'gandhi', target: 'constitution', label: 'influenced' },
          { source: 'constitution', target: 'rights', label: 'guarantees' },
          { source: 'rights', target: 'society', label: 'enables' }
        ]
      }
    },
    {
      id: 5,
      title: "The Cell and Microscope",
      beacon: {
        name: "The Cell",
        type: "Concept",
        subject: "Biology",
        description: "The basic structural and functional unit of all known living organisms, whose intricate structure could not be observed with the naked eye.",
        icon: Microscope,
        color: "from-green-500 to-emerald-500"
      },
      relationships: [
        {
          title: "Scientific Discovery",
          description: "The cell's discovery and study were made possible by the invention of the microscope, a scientific instrument that greatly magnifies small objects.",
          type: "Technological Enablement",
          icon: Microscope
        },
        {
          title: "Historical Impact",
          description: "The development and refinement of the microscope is a key event in the history of science, enabling discoveries that revolutionized Biology and Medicine.",
          type: "Cross-Subject Connection",
          icon: History
        },
        {
          title: "Scientific Progress",
          description: "This discovery fundamentally changed our understanding of life, providing a powerful example of how technology drives scientific progress and influences knowledge.",
          type: "Paradigm Shift",
          icon: Lightbulb
        }
      ],
      graphData: {
        nodes: [
          { id: 'cell', name: 'The\nCell', type: 'Concept', color: '#10b981', val: 18 },
          { id: 'microscope', name: 'Microscope', type: 'Concept', color: '#3b82f6', val: 16 },
          { id: 'biology', name: 'Biology\nRevolution', type: 'Event', color: '#8b5cf6', val: 15 },
          { id: 'medicine', name: 'Medical\nAdvances', type: 'Example', color: '#f59e0b', val: 15 },
          { id: 'technology', name: 'Scientific\nTechnology', type: 'Concept', color: '#dc2626', val: 14 }
        ],
        links: [
          { source: 'microscope', target: 'cell', label: 'revealed' },
          { source: 'cell', target: 'biology', label: 'revolutionized' },
          { source: 'cell', target: 'medicine', label: 'advanced' },
          { source: 'microscope', target: 'technology', label: 'exemplifies' },
          { source: 'technology', target: 'biology', label: 'drives progress in' }
        ]
      }
    }
  ];

  // Set page title
  useEffect(() => {
    document.title = 'Knowledge Beacons - AceHorizon';
  }, []);

  // Handle scrolling to section based on hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#available-beacons') {
      const timer = setTimeout(() => {
        const element = document.getElementById('available-beacons');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100); // Small delay to ensure DOM is fully rendered
      
      return () => clearTimeout(timer);
    }
  }, [location]);

  // Handle Start Learning Journey button click
  const handleStartLearning = () => {
    if (isAuthenticated) {
      // User is logged in, navigate to dashboard
      navigate('/dashboard');
    } else {
      // User is not logged in, navigate to signup page
      navigate('/signup');
    }
  };
  const beacons = [
    {
      title: "Core Concepts",
      description: "Understand fundamental ideas across all subjects - from Mathematical equations to Political systems, Scientific principles to Literary devices",
      level: "Cross-Subject Understanding",
      icon: Brain,
      color: "bg-violet-500",
      entityType: "Concept",
      examples: ["Linear Equation (Math)", "Democracy (Social Science)", "Motion (Science)", "Satire (English)"],
      connection: "Links theoretical knowledge across subject boundaries"
    },
    {
      title: "Historical Events", 
      description: "Explore pivotal moments, scientific discoveries, mathematical breakthroughs, and literary milestones that shaped our world",
      level: "Event Analysis",
      icon: Target,
      color: "bg-emerald-500", 
      entityType: "Event",
      examples: ["French Revolution (History)", "Gulliver's Shipwreck (Literature)", "Discovery of Equations (Math)", "Discovery of the Electron (Science)", "Rutherford's Gold Foil Experiment (Science)"],
      connection: "Connects historical context to academic concepts and notable figures"
    },
    {
      title: "Important Figures",
      description: "Connect with mathematicians, scientists, historians, writers, and political leaders who shaped human knowledge",
      level: "Character Study", 
      icon: Users,
      color: "bg-orange-500",
      entityType: "Figure", 
      examples: ["Louis XVI (History)", "Jerome K. Jerome (English)", "Newton (Science)", "Euclid (Mathematics)", "Raja Rammohan Roy (History)", "Galileo Galilei (Science)", "René Déscartes (Mathematics)", "Abraham Lincoln (Social Science)", "Viceroy (Geography)"],
      connection: "Links their contributions to concepts, events, and real-world applications"
    },
    {
      title: "Real-world Examples",
      description: "Bridge abstract concepts with concrete examples from geography, science, mathematics, and daily life experiences",
      level: "Applied Learning",
      icon: Lightbulb,
      color: "bg-blue-500",
      entityType: "Example",
      examples: ["Amazon River Basin (Geography)", "Blood Circulation (Science)", "Election Process (Civics)", "Solving 2x+5=0 (Math)", "Tea Plantations Clearing Forests (History)", "Catching a Cricket Ball (Science)"],
      connection: "Demonstrates how theoretical concepts manifest in observable, measurable phenomena"
    }
  ];

  const features = [
    {
      title: "See Entity Connections",
      description: "Watch how concepts connect to events, events link to figures, and figures relate to real-world examples in an interactive network."
    },
    {
      title: "Four Types of Understanding", 
      description: "Master abstract Concepts, historical Events, influential Figures, and concrete Examples—each building upon the others."
    },
    {
      title: "No More Memorization",
      description: "Understanding relationships between entities eliminates the need for rote learning. Know why, not just what."
    },
    {
      title: "Cross-Subject Connections",
      description: "See how literary concepts appear in history, how scientific examples connect to geography, building true interdisciplinary knowledge."
    }
  ];

  return (
    <StaticPageLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center py-5 sm:py-12">
          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-6 bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 dark:from-cyan-400 dark:via-blue-500 dark:to-purple-500 bg-clip-text text-transparent leading-tight px-2">
            Knowledge Beacons
          </h1>
                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed px-2">
            Navigate your learning journey through our four interconnected entity types. Each beacon connects 
            <span className="text-primary font-semibold"> Concepts, Events, Figures, and Examples</span> across Mathematics, Science, History, Geography, English, and Social Studies.
          </p>
        </div>

                {/* Compact Entity Types Showcase */}
        <div id="available-beacons" className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 dark:from-violet-400 dark:via-purple-400 dark:to-fuchsia-400 bg-clip-text text-transparent">
            Master the Four Learning Pillars
          </h2>
          
          {/* Compact Grid Layout */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {beacons.map((beacon, index) => {
              const IconComponent = beacon.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group border hover:border-primary/30">
                  <CardHeader className="pb-3">
                    {/* Compact Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className={`${beacon.color} rounded-lg p-2.5 text-white group-hover:scale-105 transition-transform`}>
                        <IconComponent className="h-5 w-5" />
                      </div>
                      <Badge variant="secondary" className="text-xs px-2 py-1">
                        {beacon.entityType}
                      </Badge>
                    </div>
                    
                    <CardTitle className="text-lg mb-2 group-hover:text-primary transition-colors leading-tight">
                      {beacon.title}
                    </CardTitle>
                    
                    <CardDescription className="text-sm leading-snug text-gray-600 dark:text-gray-400">
                      {beacon.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    {/* Compact Examples Section */}
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 mb-3">
                      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                        Examples ({beacon.examples.length})
                      </h4>
                      <div className="space-y-1.5">
                        {/* Always show first 3 examples */}
                        {beacon.examples.slice(0, 3).map((example, idx) => (
                          <div 
                            key={idx} 
                            className="text-xs bg-white dark:bg-gray-700 rounded px-2 py-1.5 border border-gray-200 dark:border-gray-600 hover:border-primary/30 transition-colors"
                          >
                            {example}
                          </div>
                        ))}
                        
                        {/* Show remaining examples if expanded */}
                        {expandedCards[index] && beacon.examples.slice(3).map((example, idx) => (
                          <div 
                            key={idx + 3} 
                            className="text-xs bg-white dark:bg-gray-700 rounded px-2 py-1.5 border border-gray-200 dark:border-gray-600 hover:border-primary/30 transition-colors animate-in slide-in-from-top-2 duration-200"
                          >
                            {example}
                          </div>
                        ))}
                        
                        {/* Show/Hide toggle button */}
                        {beacon.examples.length > 3 && (
                          <button
                            onClick={() => toggleCardExpansion(index)}
                            className="w-full text-xs text-primary hover:text-primary/80 italic px-2 py-1 hover:bg-primary/5 rounded transition-colors flex items-center justify-center gap-1"
                          >
                            {expandedCards[index] ? (
                              <>
                                <span>Show less examples</span>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                              </>
                            ) : (
                              <>
                                <span>+{beacon.examples.length - 3} more examples...</span>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Compact Connection Info */}
                    <div className="border-l-3 border-emerald-500 pl-3 py-2 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-r">
                      <div className="flex items-start gap-2">
                        <Network className="h-3 w-3 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-emerald-700 dark:text-emerald-300 leading-relaxed">
                          {beacon.connection}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Entity Relationships Overview - Simplified */}
          <div className="mt-12 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/10 dark:via-indigo-900/10 dark:to-purple-900/10 rounded-xl p-6">
            <h3 className="text-lg font-bold text-center mb-4 text-gray-800 dark:text-gray-200">
              How These Entity Types Connect
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  from: "Concepts",
                  to: "Examples", 
                  description: "Abstract ideas become concrete",
                  icon: "🧠 → 💡"
                },
                {
                  from: "Events",
                  to: "Figures",
                  description: "Historical moments connect to people",
                  icon: "📅 → 👤"
                },
                {
                  from: "Figures",
                  to: "Concepts",
                  description: "People develop and prove ideas",
                  icon: "👤 → 🧠"
                },
                {
                  from: "Examples",
                  to: "Events",
                  description: "Real applications shape history",
                  icon: "💡 → 📅"
                }
              ].map((connection, idx) => (
                <div key={idx} className="text-center bg-white/50 dark:bg-gray-800/30 rounded-lg p-3">
                  <div className="text-xl mb-2">{connection.icon}</div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {connection.from} → {connection.to}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 leading-tight">
                    {connection.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>


        {/* Entity Relationship Carousel */}
        <div className="mb-16 from-emerald-50 via-teal-50 to-cyan-50 dark:from-emerald-900/10 dark:via-teal-900/10 dark:to-cyan-900/10 rounded-3xl p-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 flex items-center justify-center gap-3 bg-gradient-to-r from-rose-600 via-orange-600 to-amber-600 dark:from-rose-400 dark:via-orange-400 dark:to-amber-400 bg-clip-text text-transparent">
              Interactive Knowledge Beacons
              </h2>
            <p className="text-gray-600 dark:text-gray-300 text-lg mb-2">
              <span className="font-semibold text-purple-600 dark:text-purple-400">Interactive Examples:</span> Discover how knowledge beacons create meaningful connections
            </p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">
              Explore real-world examples of cross-subject relationships and knowledge networks
            </p>
          </div>

          {/* Relationship Examples Carousel */}
          <div className="max-w-7xl mx-auto">
            <Carousel 
              opts={{ align: "start", loop: true }} 
              className="w-full"
              setApi={setCarouselApi}
            >
              <CarouselContent>
                {relationshipExamples.map((example) => {
                  const BeaconIcon = example.beacon.icon;
                  return (
                    <CarouselItem key={example.id} className="md:basis-1/1 lg:basis-1/1">
                      <div className="bg-white dark:bg-gray-800 rounded-xl border shadow-lg overflow-hidden">
                        {/* Header Section */}
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-start gap-4">
                            {/* Beacon Spotlight */}
                            <div className="flex-shrink-0">
                              <div className={`w-16 h-16 rounded-lg bg-gradient-to-br ${example.beacon.color} flex items-center justify-center text-white shadow-lg`}>
                                <BeaconIcon className="w-8 h-8" />
                              </div>
                            </div>
                            
                            {/* Beacon Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                  {example.beacon.name}
                                </h3>
                                <Badge variant="secondary" className="shrink-0">
                                  {example.beacon.type}
                                </Badge>
                                <Badge variant="outline" className="shrink-0">
                                  {example.beacon.subject}
                                </Badge>
                              </div>
                              <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                                {example.beacon.description}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Main Content Grid - 60% Graph, 40% Relationships */}
                        <div className="grid lg:grid-cols-5 gap-6 p-6">
                          {/* Knowledge Graph - 60% space (3 columns) */}
                          <div className="lg:col-span-3 space-y-4">
                            <div className="flex items-center gap-2 mb-3">
                              <Network className="w-5 h-5 text-blue-600" />
                              <h4 className="font-semibold text-gray-900 dark:text-white">
                                Knowledge Network
                              </h4>
                            </div>
                            <div className="flex justify-center">
                              <CompactD3Network data={example.graphData} width={600} height={400} />
                            </div>
                          </div>

                          {/* Relationships - 40% space (2 columns) */}
                          <div className="lg:col-span-2 space-y-4">
                            <div className="flex items-center gap-2 mb-3">
                              <ArrowRight className="w-5 h-5 text-emerald-600" />
                              <h4 className="font-semibold text-gray-900 dark:text-white">
                                Key Relationships
                              </h4>
                            </div>
                            <div className="space-y-3">
                              {example.relationships.map((relationship, idx) => {
                                const RelIcon = relationship.icon;
                                return (
                                  <div key={idx} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border-l-4 border-emerald-500">
                                    <div className="flex items-start gap-2">
                                      <div className="flex-shrink-0 w-7 h-7 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                                        <RelIcon className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1 mb-1">
                                          <h5 className="font-medium text-gray-900 dark:text-white text-sm">
                                            {relationship.title}
                                          </h5>
                                          <Badge variant="outline" className="text-xs">
                                            {relationship.type}
                                          </Badge>
                                        </div>
                                        <p className="text-gray-600 dark:text-gray-300 text-xs leading-relaxed">
                                          {relationship.description}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Footer with Navigation Hint */}
                        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                            <span className="font-medium">Example {example.id} of {relationshipExamples.length}</span>
                            <span className="flex items-center gap-1">
                              <ArrowRight className="w-3 h-3" />
                              Use arrows to explore more examples
                            </span>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselPrevious className="left-4" />
              <CarouselNext className="right-4" />
            </Carousel>
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 dark:from-emerald-400 dark:via-teal-400 dark:to-cyan-400 bg-clip-text text-transparent">
            Why Entity-Based Learning Works
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-primary rounded-full"></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-2xl p-8 text-center mb-8">
          <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-indigo-600 via-blue-600 to-teal-600 dark:from-indigo-400 dark:via-blue-400 dark:to-teal-400 bg-clip-text text-transparent">
            Ready to Explore Entity Networks?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
            Join thousands of learners who have discovered the power of understanding relationships between concepts, events, figures, and examples. 
            No more memorization—just pure understanding.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="px-8" onClick={handleStartLearning}>
              {isAuthenticated ? 'Explore Networks' : 'Start Learning Networks'}
            </Button>
          </div>
        </div>
      </div>
    </StaticPageLayout>
  );
}
